import React, {FC, useCallback, useEffect, useRef, useState} from "react";
import {Button} from "@components/ui/button.tsx";
import {Plus} from "lucide-react";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@app/components/ui/dialog";
import {Input} from "@components/ui/input.tsx";
import {useSpeedDialSet} from "@hooks/speed-dial.ts";
import {Select} from "@pages/speed-dial/components/select.tsx";
import {Label} from "@components/ui/label.tsx";
import {RuleType} from "@types";

type SetButtonProps = {
    onSet: () => void;
};

export const SetButton: FC<SetButtonProps> = React.memo(({onSet}) => {
    const [open, setOpen] = useState(false);
    const [pair, setPair] = useState<number | null>(null);
    const [slot, setSlot] = useState<number | null>(null);

    const [response, loading, error, setSpeedDial] = useSpeedDialSet();

    const hasRequested = useRef(false);
    const wasLoading = useRef(false);

    const readyToSet = () => slot != null && slot > 0 && pair != null;

    const handleCreate = () => {
        if (!readyToSet()) {
            alert("Please select a slot > 0 and a valid token pair");
            return;
        }

        hasRequested.current = true;
        setSpeedDial(slot!, pair!);
    };

    useEffect(() => {
        if (hasRequested.current && wasLoading.current && !loading && !error && response) {
            onSet();
            handleClose();
        }
        wasLoading.current = loading;
    }, [loading, error, response, onSet]);

    const handleSlotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value.trim();
        const parsedSlot = parseInt(input, 10);
        setSlot(input === '' || isNaN(parsedSlot) ? null : parsedSlot);
    };

    const handleClose = useCallback(() => {
        setOpen(false);
        setPair(null);
        setSlot(null);
        hasRequested.current = false;
    }, []);

    return (
        <>
            <Button onClick={() => setOpen(true)}>
                <Plus className="w-6 h-6"/> Set
            </Button>

            <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
                <DialogContent className="rounded-none bg-gradient-to-br from-orange-100/20 via-orange-50/10 to-transparent backdrop-blur-md shadow-xl">
                    <DialogHeader>
                        <DialogTitle className="text-orange-600">Set speed dial</DialogTitle>
                    </DialogHeader>

                    <div className="flex items-center space-x-4">
                        <Label htmlFor="slot-input" className="text-orange-200 font-medium">
                            Slot
                        </Label>
                        <Input
                            id={"slot-input"}
                            type="text"
                            placeholder="Enter slot"
                            value={slot ?? ''}
                            onChange={handleSlotChange}
                            className="rounded-lg bg-white/5 text-orange-100 placeholder-orange-100/40 border border-orange-200/40 focus:ring-2 focus:ring-orange-300 focus:outline-none backdrop-blur-sm"
                        />
                    </div>



                    <Select
                        onSelect={(pair) => {
                            setPair(pair);
                            console.log("Selected pair:", pair);
                        }}
                    />

                    <Button
                        className="mt-4"
                        onClick={handleCreate}
                        disabled={!readyToSet() || loading}
                    >
                        {loading ? "Setting..." : "Set"}
                    </Button>

                    {error && (
                        <p className="text-red-600 text-sm mt-2">
                            Failed to set speed dial: {error.message}
                        </p>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
});